import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Clock, ArrowRight, Mail } from 'lucide-react';
import { resolveAssetUrl } from '@/lib/assetUrl';
import { sanitizeHtml } from '@/lib/sanitizeHtml';

// Shared "Whistlelink-style" visual block (header + hero + protection cards +
// institutional text + action sidebar with report/track CTAs). Used both by the
// real public landing (ReportFormPage) and by the live preview in Settings.
const LandingPreviewBlock = ({
  tenantName, heroTitle, heroSubtitle, heroImageUrl, primaryColor, logoUrl,
  institutionalText, supportEmail, supportSignature, onStart, interactive = true, sticky = true,
}) => {
  const color = primaryColor || '#0F172A';
  const resolvedLogo = resolveAssetUrl(logoUrl);
  const resolvedHero = resolveAssetUrl(heroImageUrl);

  return (
    <div className="bg-slate-50">
      <header className={`w-full bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm ${sticky ? 'sticky top-0 z-50' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <span className="flex items-center gap-2 text-slate-900">
            <Shield className="w-6 h-6" />
            <span className="font-semibold hidden sm:inline">Canal de Denúncias</span>
          </span>
          {resolvedLogo ? (
            <img src={resolvedLogo} alt={tenantName} className="max-h-12 w-auto object-contain" data-testid="tenant-logo" />
          ) : (
            <span className="font-semibold text-slate-900" data-testid="tenant-logo">{tenantName}</span>
          )}
        </div>
      </header>

      <div
        className="relative w-full h-[420px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundColor: color,
          backgroundImage: resolvedHero ? `url(${resolvedHero})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-banner"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
        <div className="relative max-w-2xl mx-auto text-center text-white px-4">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">{heroTitle || 'Canal de Denúncias Interno'}</h1>
          <p className="text-lg opacity-90">{heroSubtitle || 'Submeta a sua denúncia de forma segura e confidencial'}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: Lock, title: 'Anonimato Garantido', text: 'O seu IP não é registado e todos os metadados são removidos.' },
          { icon: Shield, title: 'Proteção Legal', text: 'Lei 93/2021 garante proteção contra represálias.' },
          { icon: Clock, title: 'Prazos Legais', text: 'Confirmação de receção em 7 dias, feedback em 90 dias.' },
        ].map(({ icon: Icon, title, text }) => (
          <div key={title} className="bg-white rounded-xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 hover:-translate-y-1 transition-transform duration-300">
            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
              <Icon className="w-6 h-6 text-slate-700" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
            <p className="text-sm text-slate-600">{text}</p>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          {institutionalText ? (
            <div className="prose prose-slate prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(institutionalText) }} data-testid="institutional-text" />
          ) : (
            <p className="text-slate-600 leading-relaxed">
              Encoraja-mo-lo a denunciar qualquer situação suspeita ou irregular através deste canal.
              Garantimos total anonimato e confidencialidade ao longo de todo o processo.
            </p>
          )}
        </div>
        <div className="lg:col-span-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] sticky top-24" data-testid="landing-actions-card">
            {interactive ? (
              <button
                type="button"
                onClick={onStart}
                className="w-full px-6 py-4 rounded-lg font-semibold text-white shadow-md hover:opacity-90 transition-all duration-200 inline-flex items-center justify-center gap-2"
                style={{ backgroundColor: color }}
                data-testid="start-report-cta"
              >
                SUBMETA A DENÚNCIA AQUI
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <span
                className="w-full px-6 py-4 rounded-lg font-semibold text-white shadow-md inline-flex items-center justify-center gap-2 cursor-default"
                style={{ backgroundColor: color }}
                data-testid="start-report-cta-preview"
              >
                SUBMETA A DENÚNCIA AQUI
                <ArrowRight className="w-4 h-4" />
              </span>
            )}

            {interactive ? (
              <Link
                to="/seguimento"
                className="mt-3 w-full px-6 py-4 rounded-lg font-semibold border-2 bg-white hover:bg-slate-50 transition-colors duration-200 inline-flex items-center justify-center"
                style={{ borderColor: color, color }}
                data-testid="follow-up-link"
              >
                ACOMPANHE O SEU CASO
              </Link>
            ) : (
              <span
                className="mt-3 w-full px-6 py-4 rounded-lg font-semibold border-2 bg-white inline-flex items-center justify-center cursor-default"
                style={{ borderColor: color, color }}
                data-testid="follow-up-link-preview"
              >
                ACOMPANHE O SEU CASO
              </span>
            )}

            {(supportEmail || supportSignature) && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" style={{ color }} /> Suporte
                </h4>
                {supportSignature ? (
                  <div
                    className="prose prose-slate prose-sm max-w-none prose-p:leading-relaxed prose-a:break-all"
                    style={{ '--tw-prose-links': color }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(supportSignature) }}
                    data-testid="landing-support-signature"
                  />
                ) : (
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Se tiver alguma dúvida sobre o funcionamento do canal, contacte-nos através de{' '}
                    <a href={`mailto:${supportEmail}`} className="underline break-all" style={{ color }}>
                      {supportEmail}
                    </a>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPreviewBlock;
